import { useMemo } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import useTranslation from '../../../../hooks/useTranslation'
import { widgetFont } from '../../widgetTypography'
import AnalyticsCard from './AnalyticsCard'
import { Delta, resolveKpi } from './Kpi'
import useAnalyticsWidget from './useAnalyticsWidget'

/*
 * A handful of figures side by side, each with its change on the period
 * before — invoiced, invoices issued, quotes approved, shipments booked.
 *
 *     tiles: [{ id, label, value: <expression>, format, change, favorable, path }]
 *         as a KPI (Kpi.jsx); `path` makes the tile a link to the list behind it
 *
 * The tiles fill the card: as many to a row as fit at a readable width, so
 * four sit in a row on a wide card and two by two on a narrow one. A period
 * with nothing in it shows zeros rather than "no data" — none is an answer
 * here, and the tiles keep their places.
 */

const Grid = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 8.5rem), 1fr));
    grid-auto-rows: minmax(0, 1fr);
    gap: 0.5rem;
`

const Tile = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.2rem;
    min-width: 0;
    min-height: 0;
    padding: 0.6rem 0.75rem;
    border: 1px solid ${p => p.theme.table.border};
    border-radius: 0.75rem;
    color: ${p => p.theme.font.color};
    text-decoration: none;

    &:is(a):hover { border-color: ${p => p.theme.font.label}; }
    &:is(a):focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
`

const TileLabel = styled.span`
    ${widgetFont.label}
    color: ${p => p.theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TileValue = styled.span`
    ${widgetFont.value}
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Change = styled.span`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0 0.3rem;
    ${widgetFont.caption}
    color: ${p => p.theme.font.label};
`

const KpiTilesWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, format, compareLabel } = w

    const tiles = useMemo(() => data.current ? (config.tiles ?? []).map(k => resolveKpi(k, data, config.measures)) : [],
        [config.tiles, data, config.measures])

    // Empty is not a state here — a tile reads 0 for a quiet period
    const status = data.isLoading ? 'loading' : data.error && !data.current ? 'error' : null

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            path={config.path}
            status={status}
            error={data.error}
            onRetry={data.refetch}
        >
            <Grid>
                {tiles.map(tile => {
                    const label = t(tile.label)
                    return (
                        <Tile key={tile.id} as={tile.path ? Link : 'div'} to={tile.path} data-testid={`${type}.${tile.id}`}>
                            <TileLabel title={label}>{label}</TileLabel>
                            <TileValue>{format(tile.current, tile.format)}</TileValue>
                            {tile.delta && (
                                <Change>
                                    <Delta delta={tile.delta} kpiTone={tile.tone} format={tile.format} />
                                    <span>{t(compareLabel)}</span>
                                </Change>
                            )}
                        </Tile>
                    )
                })}
            </Grid>
        </AnalyticsCard>
    )
}

export default KpiTilesWidget
